import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import AppLayout from "@/components/layout/AppLayout";
import Login from "@/pages/Login";
import PublicRegistration from "@/pages/PublicRegistration";
import PublicLeaderboard from "@/pages/PublicLeaderboard";
import TermsAndConditions from "@/pages/TermsAndConditions";
import Dashboard from "@/pages/Dashboard";
import RegistrationManagement from "@/pages/RegistrationManagement";
import Participants from "@/pages/Participants";
import ParticipantDetail from "@/pages/ParticipantDetail";
import UploadReport from "@/pages/UploadReport";
import ImportHistory from "@/pages/ImportHistory";
import Leaderboard from "@/pages/Leaderboard";
import Milestones from "@/pages/Milestones";
import RewardsPayouts from "@/pages/RewardsPayouts";
import BaliQualifiers from "@/pages/BaliQualifiers";
import RiskMonitoring from "@/pages/RiskMonitoring";
import CRM from "@/pages/CRM";
import AuditLog from "@/pages/AuditLog";
import UserManagement from "@/pages/UserManagement";
import BrokerIntegrations from "@/pages/BrokerIntegrations";
import BrokerDocs from "@/pages/BrokerDocs";
import Settings from "@/pages/Settings";
import Analytics from "@/pages/Analytics";

function RoleGuard({ allow, children }) {
  const { user } = useAuth();
  if (user === undefined) return null;
  if (!user || !allow.includes(user.role)) return <Navigate to="/dashboard" replace />;
  return children;
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster position="top-right" theme="dark" richColors />
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<PublicRegistration />} />
          <Route path="/public-leaderboard" element={<PublicLeaderboard />} />
          <Route path="/terms" element={<TermsAndConditions />} />
          <Route element={<AppLayout />}>
            <Route path="/" element={<Navigate to="/dashboard" replace />} />
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/registrations" element={<RoleGuard allow={["super_admin", "admin_ops"]}><RegistrationManagement /></RoleGuard>} />
            <Route path="/participants" element={<Participants />} />
            <Route path="/participants/:id" element={<ParticipantDetail />} />
            <Route path="/uploads" element={<RoleGuard allow={["super_admin", "admin_ops"]}><UploadReport /></RoleGuard>} />
            <Route path="/imports" element={<ImportHistory />} />
            <Route path="/leaderboard" element={<Leaderboard />} />
            <Route path="/milestones" element={<Milestones />} />
            <Route path="/payouts" element={<RoleGuard allow={["super_admin", "admin_ops"]}><RewardsPayouts /></RoleGuard>} />
            <Route path="/bali-qualifiers" element={<BaliQualifiers />} />
            <Route path="/risk" element={<RoleGuard allow={["super_admin", "admin_ops"]}><RiskMonitoring /></RoleGuard>} />
            <Route path="/crm" element={<CRM />} />
            <Route path="/audit" element={<RoleGuard allow={["super_admin", "admin_ops"]}><AuditLog /></RoleGuard>} />
            <Route path="/integrations" element={<RoleGuard allow={["super_admin", "admin_ops", "broker_viewer"]}><BrokerIntegrations /></RoleGuard>} />
            <Route path="/broker-docs" element={<RoleGuard allow={["super_admin", "admin_ops", "broker_viewer"]}><BrokerDocs /></RoleGuard>} />
            <Route path="/users" element={<RoleGuard allow={["super_admin"]}><UserManagement /></RoleGuard>} />
            <Route path="/settings" element={<RoleGuard allow={["super_admin"]}><Settings /></RoleGuard>} />
            <Route path="/analytics" element={<RoleGuard allow={["super_admin"]}><Analytics /></RoleGuard>} />
          </Route>
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
